import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Plus, Pencil, Trash2, Star, StarOff, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { motion } from "framer-motion";
import { ProductForm } from "@/components/ProductForm";
import { ProductFilters } from "@/components/ProductFilters";
import { supabase } from "@/lib/supabase";
import { ColorBadge } from "@/components/ColorBadge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { sanitizeInventoryData } from "@/lib/utils";

const ITEMS_PER_PAGE = (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 1024px)').matches) ? 8 : 10;

export function ProductList({ readOnly = false }) {
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [showStockInfo, setShowStockInfo] = useState(null);
  const [filters, setFilters] = useState({
    temporada: null,
    tagIds: null,
    active: null
  });
  const { toast } = useToast();
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [imagePreviewSrc, setImagePreviewSrc] = useState(null);
  const [imagePreviewAlt, setImagePreviewAlt] = useState("");
  const [isDefaultTemporadaResolved, setIsDefaultTemporadaResolved] = useState(false);

  const openImagePreview = (src, alt = "Imagen del producto") => {
    setImagePreviewSrc(src);
    setImagePreviewAlt(alt);
    setIsImagePreviewOpen(true);
  };

  useEffect(() => {
    const loadDefaultTemporada = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('default_temporada_products')
          .not('title', 'is', null)
          .limit(1);

        if (error) throw error;

        const configuredTemporada = data?.[0]?.default_temporada_products?.trim?.() || data?.[0]?.default_temporada_products || null;

        if (configuredTemporada) {
          const { count, error: seasonError } = await supabase
            .from('products')
            .select('id', { count: 'exact', head: true })
            .eq('temporada', configuredTemporada)
            .limit(1);

          if (seasonError) throw seasonError;

          if ((count || 0) > 0) {
            setFilters(prev => (prev.temporada ? prev : { ...prev, temporada: configuredTemporada }));
          }
        }
      } catch (error) {
        console.error('Error loading default temporada:', error);
      } finally {
        setIsDefaultTemporadaResolved(true);
      }
    };

    loadDefaultTemporada();
  }, []);

  useEffect(() => {
    if (!isDefaultTemporadaResolved) return;
    fetchProductsData();
  }, [currentPage, searchTerm, filters, isDefaultTemporadaResolved]);

  const fetchInventory = async (productIds = []) => {
    try {
      let query = supabase
        .from('inventory')
        .select('product_id, size, color, quantity, pending_orders');
      
      // Si se proporcionan IDs de productos específicos, filtrar por ellos
      if (productIds.length > 0) {
        query = query.in('product_id', productIds);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Sanear para asegurar números consistentes
      setInventory(sanitizeInventoryData(data || []));
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el inventario",
        variant: "destructive"
      });
    }
  };

  const fetchProductsData = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('products')
        .select('id, code, description, price, active, images, main_image_index, sizes, colors, temporada, composicion, venta_solo_stock', { count: 'exact' });

      // Aplicar filtro de búsqueda
      if (searchTerm) {
        query = query.or(`code.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`);
      }

      // Aplicar filtro de temporada
      if (filters.temporada) {
        query = query.eq('temporada', filters.temporada);
      }

      // Aplicar filtro de estado activo
      if (filters.active !== null) {
        query = query.eq('active', filters.active);
      }

      // Si hay filtros de etiquetas, necesitamos hacer una consulta más compleja
      if (filters.tagIds && filters.tagIds.length > 0) {
        // Primero obtenemos los IDs de productos que tienen las etiquetas seleccionadas
        const { data: productTagsData, error: tagsError } = await supabase
          .from('product_tags')
          .select('product_id')
          .in('tag_id', filters.tagIds);

        if (tagsError) throw tagsError;

        if (productTagsData && productTagsData.length > 0) {
          const productIds = productTagsData.map(pt => pt.product_id);
          query = query.in('id', productIds);
        } else {
          // Si no hay productos con esas etiquetas, devolver array vacío
          setProducts([]);
          setTotalCount(0);
          setInventory([]);
          setIsLoading(false);
          return;
        }
      }

      const { data, error, count } = await query
        .order('code', { ascending: true })
        .range((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE - 1);

      if (error) throw error;
      setProducts(data || []);
      setTotalCount(count || 0);
      
      // Obtener el inventario solo para los productos de esta página
      if (data && data.length > 0) {
        const productIds = data.map(product => product.id);
        await fetchInventory(productIds);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los productos",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    console.log('Filtros cambiados:', newFilters); // Solo para debugging
    setFilters(newFilters);
    setCurrentPage(1); // Resetear a la primera página cuando cambien los filtros
  };

  const handleDelete = async (productId) => {
    try {
      // Verificar si hay movimientos de inventario
      const { data: inventoryData, error: inventoryError } = await supabase
        .from('inventory')
        .select('id')
        .eq('product_id', productId)
        .limit(1);

      if (inventoryError) throw inventoryError;

      if (inventoryData && inventoryData.length > 0) {
        toast({
          title: "Error",
          description: "No se puede eliminar el producto porque tiene movimientos de inventario asociados",
          variant: "destructive"
        });
        return;
      }

      // Eliminar el producto
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;

      await fetchProductsData();
      toast({
        title: "Producto eliminado",
        description: "El producto se ha eliminado exitosamente",
        variant: "default"
      });
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el producto",
        variant: "destructive"
      });
    }
  };

  const handleToggleActive = async (product) => {
    try {
      const { error } = await supabase
        .from('products')
        .update({ active: !product.active })
        .eq('id', product.id);

      if (error) throw error;

      await fetchProductsData();
      toast({
        title: "Estado actualizado",
        description: `Producto ${product.active ? 'desactivado' : 'activado'} exitosamente`,
      });
    } catch (error) {
      console.error('Error toggling product status:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el estado del producto",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingProduct(null);
    fetchProductsData();
  };

  // Los filtros se aplican en la API, no necesitamos filtrado local adicional
  const filteredProducts = products;

  if (showForm) {
    return <ProductForm onClose={handleFormClose} product={editingProduct} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <Input
          type="search"
          placeholder="Buscar productos..."
          className="max-w-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {!readOnly && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="mr-2 h-4 w-4" /> Nuevo Producto
          </Button>
        )}
      </div>

      {/* Filtros de productos */}
      <ProductFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        showActive={true}
      />

      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {filteredProducts.map((product) => {
              // Crear un mapa de inventario para acceso más rápido
              const inventoryMap = new Map();
              inventory
                .filter(i => i.product_id === product.id)
                .forEach(item => {
                  const key = `${item.color}::${item.size}`;
                  inventoryMap.set(key, item);
                });

              // Prepara el stock por color y talla para la tabla
              const stockPorColor = (product.colors || []).map(color => {
                const stockPorTalla = (product.sizes || []).map(size => {
                  const key = `${color}::${size}`;
                  const item = inventoryMap.get(key);
                  const stockDisponible = item ? (item.quantity || 0) - (item.pending_orders || 0) : 0;
                  
                  return stockDisponible;
                });
                return { color, stockPorTalla };
              });

              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white p-4 rounded-lg shadow-sm"
                >
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Columna 1: Imagen y datos generales */}
                    <div className="flex-1 flex gap-4 min-w-[250px]">
                      {product.images && product.images.length > 0 && (
                        <button
                          type="button"
                          className="w-24 h-24 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
                          onClick={() => {
                            const src = product.images[product.main_image_index || 0];
                            const alt = product.description || "Imagen del producto";
                            if (src) {
                              openImagePreview(src, alt);
                            }
                          }}
                          aria-label="Ver imagen en grande"
                        >
                          <img
                            src={product.images[product.main_image_index || 0]}
                            alt={product.description}
                            className="w-full h-full object-cover rounded"
                          />
                        </button>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{product.code}</h3>
                          {!product.active && (
                            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                              Inactivo
                            </span>
                          )}
                          {product.venta_solo_stock && (
                            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                              Solo Venta Stock
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{product.description}</p>
                        <p className="text-sm font-medium mt-1">€{product.price}</p>
                        {product.temporada && (
                          <p className="text-sm text-gray-600 mt-1">
                            Temporada: {product.temporada}
                          </p>
                        )}
                        {product.composicion && (
                          <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">
                            Composición: {product.composicion}
                          </p>
                        )}
                        {/* Tallas */}
                        <div className="flex gap-2 mt-2 mb-1" translate="no">
                          {(product.sizes || []).map(size => (
                            <span key={size} className="text-xs font-bold bg-gray-100 px-2 py-1 rounded" translate="no">
                              {size}
                            </span>
                          ))}
                        </div>
                        {/* Colores */}
                        <div className="flex gap-2 mb-2">
                          {(product.colors || []).map(color => (
                            <ColorBadge key={color} color={color} />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Columna 2: Tabla de stock */}
                    <div className="min-w-[350px] overflow-x-auto">
                      <div className="text-sm font-medium mb-2 flex items-center gap-2">
                        Stock por Color/Talla
                        
                        <button
                          type="button"
                          className="ml-1 text-gray-400 hover:text-gray-700 focus:outline-none"
                          onClick={() => setShowStockInfo(product.id)}
                        >
                          <Info className="w-4 h-4" />
                        </button>
                        {showStockInfo === product.id && (
                          <div className="absolute z-50 bg-white border border-gray-300 rounded shadow-lg p-3 text-xs text-gray-700 max-w-xs mt-8 ml-2">
                            Las columnas de los stocks por tallas y colores muestran las cantidades disponibles de stocks, que se calcula teniendo en cuenta el stock físico y las cantidades que hay pendientes de servir de pedidos clientes acumulados.
                            <button className="block mt-2 text-blue-600 hover:underline" onClick={() => setShowStockInfo(null)}>Cerrar</button>
                          </div>
                        )}
                      </div>
                      <table className="min-w-full text-center border border-gray-200 rounded">
                        <thead>
                          <tr>
                            <th className="px-2 py-1 border-b border-gray-200">Color</th>
                            {(product.sizes || []).map(size => (
                              <th key={size} className="px-2 py-1 border-b border-gray-200" translate="no">{size}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {stockPorColor.map(({ color, stockPorTalla }) => (
                            <tr key={color}>
                              <td className="px-2 py-1 border-b border-gray-100">
                                <ColorBadge color={color} />
                              </td>
                              {stockPorTalla.map((qty, idx) => (
                                <td
                                  key={idx}
                                  className={`px-2 py-1 border-b border-gray-100 ${qty > 0 ? 'text-green-600' : 'text-red-600'} md:px-4 lg:px-6`}
                                >
                                  {qty}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Acciones */}
                    {!readOnly && (
                      <div className="flex flex-col gap-2 justify-center ml-4">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleToggleActive(product)}
                        >
                          {product.active ? (
                            <Star className="h-4 w-4" />
                          ) : (
                            <StarOff className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleEdit(product)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="icon"
                          onClick={() => handleDelete(product.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="flex justify-between items-center mt-4">
            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Anterior
            </Button>
            <span className="text-sm text-gray-600">
              Página {currentPage} de {Math.ceil(totalCount / ITEMS_PER_PAGE)}
            </span>
            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={currentPage >= Math.ceil(totalCount / ITEMS_PER_PAGE)}
            >
              Siguiente
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          {/* Modal de vista previa de imagen */}
          <Dialog open={isImagePreviewOpen} onOpenChange={setIsImagePreviewOpen}>
            <DialogContent className="max-w-[90vw] md:max-w-3xl p-2 bg-black">
              {imagePreviewSrc && (
                <div className="w-full h-full flex items-center justify-center">
                  <img
                    src={imagePreviewSrc}
                    alt={imagePreviewAlt}
                    className="max-h-[80vh] max-w-full object-contain"
                    draggable={false}
                  />
                </div>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
